import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { ChildSummary } from '../types/assessment.types';

interface UseChildSummaryResult {
  summary: ChildSummary | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch child overall summary with automatic polling during generation.
 * - Returns summary if available immediately.
 * - Polls every 3 seconds if generation is in progress.
 * - Sets error='FAILED' and hides section on any failure.
 */
export function useChildSummary(childId: number | null): UseChildSummaryResult {
  const [summary, setSummary] = useState<ChildSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!childId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const data = await api.getChildSummary(childId);

      // If the latest job failed, hide the existing summary
      try {
        const status = await api.getChildSummaryStatus(childId);
        if (status.latestJobStatus === 'FAILED') {
          setSummary(null);
          setError('FAILED');
          setIsLoading(false);
          return;
        }
      } catch {
        // If status check fails, still show existing summary
      }

      setSummary(data);
      setIsGenerating(false);
      setIsLoading(false);
    } catch (err) {
      if (err instanceof Error && err.message === 'NO_SUMMARY') {
        try {
          const status = await api.getChildSummaryStatus(childId);
          if (status.status === 'generating') {
            setIsGenerating(true);
            setSummary(null);
          } else {
            setSummary(null);
            setIsGenerating(false);
          }
        } catch {
          setError('FAILED');
        }
      } else {
        setError('FAILED');
      }
      setIsLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Poll every 3 seconds while generating
  useEffect(() => {
    if (!isGenerating || !childId) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await api.getChildSummaryStatus(childId);

        if (status.status === 'completed' && status.hasSummary) {
          fetchSummary();
        } else if (status.latestJobStatus === 'FAILED') {
          setIsGenerating(false);
          setError('FAILED');
          clearInterval(pollInterval);
        }
      } catch {
        // Polling errors are silent — keep trying
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isGenerating, childId, fetchSummary]);

  return { summary, isLoading, isGenerating, error, refetch: fetchSummary };
}
